import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useAdminAuthStore } from "@/stores/adminAuthStore";
import { useClinicianAuthStore } from "@/stores/clinicianAuthStore";
// Side-effect import — registers the realtime socket's auth-sync subscription
// (see socketClient.ts) before silentRestore() below can resolve a token.
import "@/lib/socketClient";
import { CaregiverProtectedLayout } from "@/portals/caregiver/layout/CaregiverProtectedLayout";
import { CaregiverDashboardPage } from "@/portals/caregiver/pages/CaregiverDashboardPage";
import { AccessActivityPage } from "@/portals/caregiver/pages/AccessActivityPage";
import { CaregiverProfilePage } from "@/portals/caregiver/pages/CaregiverProfilePage";
import { CaregiverCategoryRecordsPage } from "@/portals/caregiver/pages/CaregiverCategoryRecordsPage";
import { PatientGuestOnly } from "@/portals/patient/layout/PatientGuestOnly";
import { AuthFocusLayout } from "@/shared/layout/AuthFocusLayout";
import { PatientProtectedLayout } from "@/portals/patient/layout/PatientProtectedLayout";
import { PatientSignInPage } from "@/portals/patient/pages/auth/PatientSignInPage";
import { PatientSignUpPage } from "@/portals/patient/pages/auth/PatientSignUpPage";
import { VerifyOtpPage } from "@/portals/patient/pages/auth/VerifyOtpPage";
import { ForgotPasswordPage } from "@/portals/patient/pages/auth/ForgotPasswordPage";
import { VerifyResetOtpPage } from "@/portals/patient/pages/auth/VerifyResetOtpPage";
import { ResetPasswordPage } from "@/portals/patient/pages/auth/ResetPasswordPage";
import { DeclineInvitePage } from "@/portals/patient/pages/auth/DeclineInvitePage";
import { GoogleCallbackPage } from "@/portals/patient/pages/auth/GoogleCallbackPage";
import { GoogleCompleteSignupPage } from "@/portals/patient/pages/auth/GoogleCompleteSignupPage";
import { AdminProtectedLayout } from "@/portals/admin/layout/AdminProtectedLayout";
import { AdminGuestOnly } from "@/portals/admin/layout/AdminGuestOnly";
import { sampleProfiles } from "@/data/patient/records.mock";
import { DashboardPage } from "@/portals/patient/pages/DashboardPage";
import { UploadPage } from "@/portals/patient/pages/UploadPage";
import { UploadHistoryPage } from "@/portals/patient/pages/UploadHistoryPage";
import { HumanReviewQueuePage } from "@/portals/patient/pages/HumanReviewQueuePage";
import { BulkReviewPage } from "@/portals/patient/pages/BulkReviewPage";
import { ConsentPrivacyPage } from "@/portals/patient/pages/ConsentPrivacyPage";
import { RecordDetailsPage } from "@/portals/patient/pages/RecordDetailsPage";
import { TimelinePage } from "@/portals/patient/pages/TimelinePage";
import { AskPage } from "@/portals/patient/pages/AskPage";
import { DiagnosisPage } from "@/portals/patient/pages/DiagnosisPage";
import { HealthConcernsPage } from "@/portals/patient/pages/HealthConcernsPage";
import { CompleteHistoryPage } from "@/portals/patient/pages/CompleteHistoryPage";
import { MedicationsPage } from "@/portals/patient/pages/MedicationsPage";
import { ReviewItemPage } from "@/portals/patient/pages/ReviewItemPage";
import { DiagnosticsPage } from "@/portals/patient/pages/DiagnosticsPage";
import { DicomViewerPage } from "@/portals/patient/pages/DicomViewerPage";
import { CareTreatmentPage } from "@/portals/patient/pages/CareTreatmentPage";
import { InsuranceCardsPage } from "@/portals/patient/pages/InsuranceCardsPage";
import { ClaimsPage } from "@/portals/patient/pages/ClaimsPage";
import { FhirExportPage } from "@/portals/patient/pages/FhirExportPage";
import { ManageFamilyPage } from "@/portals/patient/pages/ManageFamilyPage";
import { ManageCaregiversPage } from "@/portals/patient/pages/ManageCaregiversPage";
import { ProfilePage } from "@/portals/patient/pages/profile/ProfilePage";
import { AdminSignInPage } from "@/portals/admin/pages/AdminSignInPage";
import { AdminDashboardPage } from "@/portals/admin/pages/AdminDashboardPage";
import { UsersPage } from "@/portals/admin/pages/UsersPage";
import { AuditTrailPage } from "@/portals/admin/pages/AuditTrailPage";
import { CliniciansPage } from "@/portals/admin/pages/CliniciansPage";
import { PatientDetailPage } from "@/portals/admin/pages/PatientDetailPage";
import { ReviewQueuePage } from "@/portals/admin/pages/ReviewQueuePage";
import { ReviewQueueItemPage } from "@/portals/admin/pages/ReviewQueueItemPage";
import { AssignedItemsPage } from "@/portals/admin/pages/AssignedItemsPage";
import { AssignedItemPage } from "@/portals/admin/pages/AssignedItemPage";
import { ClinicianGuestOnly } from "@/portals/clinician/layout/ClinicianGuestOnly";
import { ClinicianProtectedLayout } from "@/portals/clinician/layout/ClinicianProtectedLayout";
import { ClinicianLoginPage } from "@/portals/clinician/pages/ClinicianLoginPage";
import { ClinicianInvitePage } from "@/portals/clinician/pages/ClinicianInvitePage";
import { ClinicianDeclineInvitePage } from "@/portals/clinician/pages/ClinicianDeclineInvitePage";
import { ClinicianDashboardPage } from "@/portals/clinician/pages/ClinicianDashboardPage";
import { ClinicianReviewItemPage } from "@/portals/clinician/pages/ClinicianReviewItemPage";
import { ClinicianQueuePreviewPage } from "@/portals/clinician/pages/ClinicianQueuePreviewPage";
import { ClinicianMyItemsPage } from "@/portals/clinician/pages/ClinicianMyItemsPage";
import { PrivacyPolicyPage, TermsOfUsePage, HipaaNoticePage } from "@/portals/public/pages/LegalPolicyPages";
import { LandingPage } from "@/portals/public/pages/LandingPage";

const activeProfile = sampleProfiles[0];

// Dev-only preview of Care & Treatment against a made-up patient
// (no sign-in, no backend). Not part of a production build.
const CareTreatmentPreview = import.meta.env.DEV
  ? lazy(() => import("@/features/patient/care-treatment/dev/CareTreatmentPreview"))
  : null;

// The backend's /auth/epic/callback (router.py) redirects the real Epic OAuth round
// trip to a hardcoded `${FRONTEND_BASE_URL}/dashboard?epic_connected=1` (or
// `?epic_error=<CODE>`) — this app's actual dashboard route is `/patient`, so
// without this alias that redirect 404s into the sign-in page and the status
// params (and the toast/sync they're supposed to trigger) are silently lost.
function DashboardRedirect() {
  const location = useLocation();
  return <Navigate to={`/patient${location.search}`} replace />;
}


/** /physician/<rest>?<query> -> /clinician/<rest>?<query> (the portal's old address). */
export function LegacyPhysicianRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/physician/, "/clinician") + search + hash} replace />;
}
export function AppRouter() {
  // Zustand stores don't need a Provider — kick off only the current portal's
  // silent, cookie-based session restore here instead. Only one portal is ever
  // rendered at a time, so restoring the other one's session too just wastes a
  // request and guarantees a 401 in the logs (no cookie for a portal you've
  // never logged into). Reads window.location directly rather than
  // useLocation() since this runs once on mount, before this component's own
  // BrowserRouter below exists as a context.
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) {
      void useAdminAuthStore.getState().silentRestore();
    } else if (window.location.pathname.startsWith("/clinician")) {
      void useClinicianAuthStore.getState().silentRestore();
    } else {
      // The caregiver portal reuses the patient portal's auth session
      // wholesale (see CaregiverProtectedLayout) — no separate caregiver
      // identity exists yet, so /caregiver falls in here too.
      void usePatientAuthStore.getState().silentRestore();
    }
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary variant="app">
      <Routes>
        <Route path="/" element={<Navigate to="/about" replace />} />
        {/* Public, unauthenticated marketing/home page. This URL was submitted to
            Epic as the app's "Public Documentation URL", so it must stay public and
            outside every guard below. */}
        <Route path="/about" element={<LandingPage />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/hipaa-notice" element={<HipaaNoticePage />} />
        {CareTreatmentPreview && (
          <Route path="/dev/care-treatment" element={<Suspense fallback={null}><CareTreatmentPreview /></Suspense>} />
        )}
        {/* Google's redirect target (Authorized redirect URI in the Google Cloud
            console). Outside PatientGuestOnly: it must finish even if the
            session restore below is still running. */}
        <Route element={<AuthFocusLayout />}>
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
        </Route>

        {/* Patient portal */}
        <Route path="/patient">
          {/* Guest-only auth pages — all share AuthFocusLayout via Outlet */}
          <Route path="auth" element={<PatientGuestOnly />}>
            <Route element={<AuthFocusLayout />}>
              <Route path="signin" element={<PatientSignInPage />} />
              <Route path="signup" element={<PatientSignUpPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="verify-otp" element={<VerifyOtpPage />} />
              <Route path="verify-reset-otp" element={<VerifyResetOtpPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="google/complete" element={<GoogleCompleteSignupPage />} />
            </Route>
          </Route>

          {/* Decline-without-login — deliberately outside PatientGuestOnly, since
              anyone (logged in or not, as any account) can click this email link. */}
          <Route element={<AuthFocusLayout />}>
            <Route path="invite/decline" element={<DeclineInvitePage />} />
          </Route>

          {/* Protected patient pages */}
          <Route element={<PatientProtectedLayout profile={activeProfile} />}>
            <Route index element={<DashboardPage />} />
            {/* "Document Panel" — every extracted file, browsable, alongside
                its extraction. Was "/patient/upload/history" ("Your
                Uploads"); renamed here as the single home for this content. */}
            <Route path="documents" element={<UploadHistoryPage />} />
            <Route path="profile" element={<ProfilePage profile={activeProfile} />} />
            <Route path="upload" element={<UploadPage />} />
            <Route path="human-review-queue" element={<HumanReviewQueuePage queue="health" />} />
            <Route path="human-review-queue/review-all" element={<BulkReviewPage queue="health" />} />
            {/* Identity checks ("Is this upload yours?"): patient-only, same design as the HRQ. */}
            <Route path="patient-only-queue" element={<HumanReviewQueuePage queue="identity" />} />
            <Route path="patient-only-queue/review-all" element={<BulkReviewPage queue="identity" />} />
            <Route path="records/:recordId" element={<RecordDetailsPage />} />
            <Route path="timeline" element={<TimelinePage />} />
            <Route path="ask" element={<AskPage />} />
            <Route path="consent-privacy" element={<ConsentPrivacyPage />} />
            <Route path="diagnosis" element={<DiagnosisPage />} />
            <Route path="health-concerns" element={<HealthConcernsPage />} />
            <Route path="complete-history" element={<CompleteHistoryPage />} />
            <Route path="medications" element={<MedicationsPage />} />
            <Route path="medications/review/:itemId" element={<ReviewItemPage />} />
            <Route path="diagnostics" element={<DiagnosticsPage />} />
            {/* Not a sidebar nav destination — reached only by clicking a
                study in Recent DICOM Uploads or right after a new upload
                (see DiagnosticsImagingView.tsx's openViewer). Keeps the
                normal sidebar/header chrome like every other page here — it
                just replaces the Diagnostics & Imaging content area, with
                its own back button returning to /patient/diagnostics. */}
            <Route path="diagnostics/imaging/:studyId" element={<DicomViewerPage />} />
            <Route path="care-treatment" element={<CareTreatmentPage />} />
            <Route path="insurance-cards" element={<InsuranceCardsPage />} />
            <Route path="claims" element={<ClaimsPage />} />
            <Route path="manage-family" element={<ManageFamilyPage />} />
            <Route path="manage-caregivers" element={<ManageCaregiversPage />} />
            <Route path="fhir-export" element={<FhirExportPage />} />
          </Route>
        </Route>

        {/* Admin portal */}
        <Route path="/admin">
          <Route
            path="signin"
            element={
              <AdminGuestOnly>
                <AdminSignInPage />
              </AdminGuestOnly>
            }
          />
          <Route element={<AdminProtectedLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:patientId" element={<PatientDetailPage />} />
            <Route path="audit-trail" element={<AuditTrailPage />} />
            <Route path="clinicians" element={<CliniciansPage />} />
            <Route path="review-queue" element={<ReviewQueuePage />} />
            <Route path="review-queue/:itemId" element={<ReviewQueueItemPage />} />
            <Route path="assigned-items" element={<AssignedItemsPage />} />
            <Route path="assigned-items/:itemId" element={<AssignedItemPage />} />
          </Route>
        </Route>

        {/* Caregiver portal */}
        <Route path="/caregiver">
          <Route element={<CaregiverProtectedLayout />}>
            <Route index element={<CaregiverDashboardPage />} />
            <Route path="patients/:patientId/:category" element={<CaregiverCategoryRecordsPage />} />
            <Route path="access-activity" element={<AccessActivityPage />} />
            <Route path="profile" element={<CaregiverProfilePage />} />
          </Route>
        </Route>

        {/* Clinician portal — invite-only: accounts come from an admin's
            invitation link, so there is no public signup route. */}
        <Route path="/clinician">
          <Route element={<AuthFocusLayout />}>
            <Route
              path="login"
              element={
                <ClinicianGuestOnly>
                  <ClinicianLoginPage />
                </ClinicianGuestOnly>
              }
            />
            <Route path="invite/:token" element={<ClinicianInvitePage />} />
            <Route path="invite/:token/decline" element={<ClinicianDeclineInvitePage />} />
          </Route>
          <Route element={<ClinicianProtectedLayout />}>
            <Route index element={<ClinicianDashboardPage />} />
            <Route path="queue/:itemId" element={<ClinicianQueuePreviewPage />} />
            <Route path="review/:itemId" element={<ClinicianReviewItemPage />} />
            <Route path="my-items" element={<ClinicianMyItemsPage />} />
            <Route path="my-items/:itemId" element={<ClinicianReviewItemPage />} />
          </Route>
        </Route>

        {/* The portal was renamed physician -> clinician: old links (invite emails
            already sent, bookmarks) keep working by moving to the same page. */}
        <Route path="/physician/*" element={<LegacyPhysicianRedirect />} />

        <Route path="*" element={<Navigate to="/patient/auth/signin" replace />} />
      </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
